// react
import { createContext, Dispatch, SetStateAction } from 'react';

// types
import { Document, Entity, Pagination, Transaction, User } from '../../../@types';

// defaults
import { defaultPagination } from '../../../utils/defaults';

// entity
export interface ContextEntityProps {
	entity: Entity | null;
	isLoadingEntity: boolean;
	refetchEntity: () => void;
	setEntity: Dispatch<SetStateAction<Entity | null>>;
}

export const ContextEntity = createContext<ContextEntityProps>({
	entity: null,
	isLoadingEntity: false,
	refetchEntity: () => {
		console.log('REFETCH ENTITY');
	},
	setEntity: () => {
		console.log('SET ENTITY');
	},
});

// beneficiaries
export interface ContextEntityBeneficiariesProps {
	beneficiaries: Entity[];
	isLoadingBeneficiaries: boolean;
	paginationBeneficiaries: Pagination;
	refetchBeneficiaries: () => void;
	setBeneficiaries: Dispatch<SetStateAction<Entity[]>>;
	setPaginationBeneficiaries: Dispatch<SetStateAction<Pagination>>;
}

export const ContextEntityBeneficiaries = createContext<ContextEntityBeneficiariesProps>({
	beneficiaries: [],
	isLoadingBeneficiaries: false,
	paginationBeneficiaries: defaultPagination,
	refetchBeneficiaries: () => {
		console.log('REFETCH BENEFICIARIES');
	},
	setPaginationBeneficiaries: () => {
		console.log('SET PAGINATION');
	},
	setBeneficiaries: () => {
		console.log('SET BENEFICIARIES');
	},
});

// suppliers
export interface ContextEntitySuppliersProps {
	isLoadingSuppliers: boolean;
	paginationSuppliers: Pagination;
	refetchSuppliers: () => void;
	setPaginationSuppliers: Dispatch<SetStateAction<Pagination>>;
	setSuppliers: Dispatch<SetStateAction<Entity[]>>;
	suppliers: Entity[];
}

export const ContextEntitySuppliers = createContext<ContextEntitySuppliersProps>({
	isLoadingSuppliers: false,
	paginationSuppliers: defaultPagination,
	refetchSuppliers: () => {
		console.log('REFETCH SUPPLIERS');
	},
	setPaginationSuppliers: () => {
		console.log('SET PAGINATION');
	},
	setSuppliers: () => {
		console.log('SET SUPPLIERS');
	},
	suppliers: [],
});

// payments
export interface ContextEntityPaymentsProps {
	isLoadingPayments: boolean;
	paginationPayments: Pagination;
	payments: Transaction[];
	refetchPayments: () => void;
	setPaginationPayments: Dispatch<SetStateAction<Pagination>>;
	setPayments: Dispatch<SetStateAction<Transaction[]>>;
}

export const ContextEntityPayments = createContext<ContextEntityPaymentsProps>({
	isLoadingPayments: false,
	paginationPayments: defaultPagination,
	payments: [],
	refetchPayments: () => {
		console.log('REFETCH PAYMENTS');
	},
	setPaginationPayments: () => {
		console.log('SET PAGINATION');
	},
	setPayments: () => {
		console.log('SET PAYMENTS');
	},
});

// cash benefits
export interface ContextEntityCashBenefitsProps {
	cashBenefits: Transaction[];
	isLoadingCashBenefits: boolean;
	paginationCashBenefits: Pagination;
	refetchCashBenefits: () => void;
	setCashBenefits: Dispatch<SetStateAction<Transaction[]>>;
	setPaginationCashBenefits: Dispatch<SetStateAction<Pagination>>;
}

export const ContextEntityCashBenefits = createContext<ContextEntityCashBenefitsProps>({
	cashBenefits: [],
	isLoadingCashBenefits: false,
	paginationCashBenefits: defaultPagination,
	refetchCashBenefits: () => {
		console.log('REFETCH CASHBENEFITS');
	},
	setCashBenefits: () => {
		console.log('SET CASHBENEFITS');
	},
	setPaginationCashBenefits: () => {
		console.log('SET PAGINATION');
	},
});

// non cash benefits
export interface ContextEntityNonCashBenefitsProps {
	isLoadingNonCashBenefits: boolean;
	nonCashBenefits: Transaction[];
	paginationNonCashBenefits: Pagination;
	refetchNonCashBenefits: () => void;
	setNonCashBenefits: Dispatch<SetStateAction<Transaction[]>>;
	setPaginationNonCashBenefits: Dispatch<SetStateAction<Pagination>>;
}

export const ContextEntityNonCashBenefits = createContext<ContextEntityNonCashBenefitsProps>({
	isLoadingNonCashBenefits: false,
	nonCashBenefits: [],
	paginationNonCashBenefits: defaultPagination,
	refetchNonCashBenefits: () => {
		console.log('REFETCH NONCASHBENEFITS');
	},
	setNonCashBenefits: () => {
		console.log('SET NONCASHBENEFITS');
	},
	setPaginationNonCashBenefits: () => {
		console.log('SET PAGINATION');
	},
});

// opex
export interface ContextEntityOpExProps {
	isLoadingOpEx: boolean;
	opEx: Transaction[];
	paginationOpEx: Pagination;
	refetchOpEx: () => void;
	setOpEx: Dispatch<SetStateAction<Transaction[]>>;
	setPaginationOpEx: Dispatch<SetStateAction<Pagination>>;
}

export const ContextEntityOpEx = createContext<ContextEntityOpExProps>({
	isLoadingOpEx: false,
	opEx: [],
	paginationOpEx: defaultPagination,
	refetchOpEx: () => {
		console.log('REFETCH OPEX');
	},
	setOpEx: () => {
		console.log('SET OPEX');
	},
	setPaginationOpEx: () => {
		console.log('SET PAGINATION');
	},
});

// users
export interface ContextEntityUsersProps {
	isLoadingUsers: boolean;
	paginationUsers: Pagination;
	users: User[];
	refetchUsers: () => void;
	setPaginationUsers: Dispatch<SetStateAction<Pagination>>;
	setUsers: Dispatch<SetStateAction<User[]>>;
}

export const ContextEntityUsers = createContext<ContextEntityUsersProps>({
	isLoadingUsers: false,
	paginationUsers: defaultPagination,
	refetchUsers: () => {
		console.log('REFETCH USERS');
	},
	setPaginationUsers: () => {
		console.log('SET PAGINATION');
	},
	setUsers: () => {
		console.log('SET USERS');
	},
	users: [],
});

// documents
export interface ContextEntityDocumentsProps {
	documents: Document[];
	isLoadingDocuments: boolean;
	paginationDocuments: Pagination;
	setDocuments: Dispatch<SetStateAction<Document[]>>;
	setPaginationDocuments: Dispatch<SetStateAction<Pagination>>;
	refetchDocuments: () => void;
}

export const ContextEntityDocuments = createContext<ContextEntityDocumentsProps>({
	documents: [],
	isLoadingDocuments: false,
	paginationDocuments: defaultPagination,
	setPaginationDocuments: () => {
		console.log('SET PAGINATION');
	},
	setDocuments: () => {
		console.log('SET DOCUMENTS');
	},
	refetchDocuments: () => {
		console.log('REFETCH DOCUMENTS');
	},
});
